import {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {} from "underscore";
import { gte, prerelease } from "semver";

import { getOS } from "./utils";
import FirmwareList from "./FirmwareList";
import DownloadDropdown, { BundleDownloadButton } from "./DownloadDropdown";
import Icon from "./icons";

import "./ProductionReleases.css";

const PageContext = createContext({
  settings: {},
  setSettings: () => {},
});

// 2. Custom hook for safe & clean access
function usePageContext() {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error("usePageContext must be used within PageProvider");
  }
  return context;
}

/**
 * Provides page-level state and settings to its children via React Context.
 * @param {children} param (React) DOM nodes to be rendered within the provider
 * @returns {JSX.Element} The PageContext provider wrapping the children
 */
function PageProvider({ children }) {
  const [settings, updateSettings] = useState({
    showAllVersions: false,
    originalReleases: [],
    releases: [],
    latestSupportedRelease: null,
    os: getOS(),
  });

  const setSettings = (newSettings) => {
    updateSettings((prevSettings) => ({
      ...prevSettings,
      ...newSettings,
    }));
  };

  return (
    <PageContext.Provider value={{ settings, setSettings }}>
      {children}
    </PageContext.Provider>
  );
}

const BADGE = {
  alpha: {
    name: "alpha",
    className: "text-bg-warning",
    text: "Alpha",
  },
  beta: {
    name: "beta",
    className: "text-bg-info",
    text: "Beta",
  },
  unsupported: {
    name: "unsupported",
    className: "text-bg-secondary",
    text: "Unsupported",
  },
  stable: {
    name: "stable",
    className: "text-bg-success",
    text: "Stable",
  },
};

function releaseTypeBadges(release, singleBadge = false) {
  const releaseType = [];

  if (singleBadge) {
    switch (true) {
      case !release.supported:
        releaseType.push(BADGE.unsupported);
        break;
      case prerelease(release.version)?.[0]?.includes("alpha"):
        releaseType.push(BADGE.alpha);
        break;
      case prerelease(release.version)?.[0]?.includes("beta"):
        releaseType.push(BADGE.beta);
        break;
      case isStableSupportedRelease(release):
        releaseType.push(BADGE.stable);
        break;
      default:
        break;
    }
  } else {
    if (prerelease(release.version)) {
      const pre = prerelease(release.version)[0];
      if (pre.includes("alpha")) {
        releaseType.push(BADGE.alpha);
      } else if (pre.includes("beta")) {
        releaseType.push(BADGE.beta);
      }
    }

    if (isStableSupportedRelease(release)) {
      releaseType.push(BADGE.stable);
    }

    if (!release.supported) {
      releaseType.push(BADGE.unsupported);
    }
  }

  return releaseType;
}

function formatDate(date) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function sanitizeVersion(version) {
  return version.split(".").length < 3 ? `${version}.0` : version;
}

function versionTitle(release) {
  return `UI ${release.dependencies["oblac-drives"]} and MM ${release.dependencies["motion-master"]}`;
}

function isStableSupportedRelease(release) {
  return release && release.supported && !prerelease(release.version);
}

function SearchContent() {
  const searchInputRef = useRef(null);
  const searchBtnRef = useRef(null);
  const { settings, setSettings } = usePageContext();
  const { originalReleases, releases } = settings;

  const highlightTerm = useCallback((content, positions, queryLength) => {
    if (content.length && positions.length) {
      return positions.reduce((acc, pos) => {
        acc =
          content.slice(0, pos) +
          "<mark>" +
          content.slice(pos, pos + queryLength) +
          "</mark>" +
          content.slice(pos + queryLength, content.length);

        return acc;
      }, "");
    }

    return content;
  });

  const findAllTermPositions = useCallback((str, term) => {
    const positions = [];
    let pos = str.indexOf(term);

    while (pos !== -1) {
      positions.push(pos);
      pos = str.indexOf(term, pos + term.length);
    }

    return positions;
  });

  const handleInput = useCallback(() => {
    const query = searchInputRef.current.value.trim();

    if (query === "" || !originalReleases?.length) {
      setSettings({ releases: originalReleases });
      return;
    } else {
      setSettings({ showAllVersions: true });
    }

    const filteredReleases = JSON.parse(JSON.stringify(originalReleases || []))
      ?.filter((release) =>
        release.description?.toLowerCase().includes(query.toLowerCase()),
      )
      .map((release) => {
        if (release && release.description) {
          const positions = findAllTermPositions(
            release.description.toLowerCase(),
            query.toLowerCase(),
          );
          release.description = highlightTerm(
            release.description,
            positions,
            query.length,
          );
        }

        return release;
      });
    setSettings({ releases: filteredReleases || [] });
  });

  useEffect(() => {
    searchInputRef.current?.addEventListener("search", handleInput);
    searchBtnRef.current?.addEventListener("click", handleInput);

    return () => {
      searchInputRef.current?.removeEventListener("search", handleInput);
      searchBtnRef.current?.removeEventListener("click", handleInput);
    };
  }, [searchInputRef.current, originalReleases]);

  return (
    <div className="input-group align-items-center w-auto">
      {searchInputRef.current?.value.trim().length ? (
        <span className="form-control-plaintext w-auto me-2">
          {releases.length} releases contain this term
        </span>
      ) : null}
      <input
        ref={searchInputRef}
        type="search"
        placeholder="Search the changelog..."
        className="form-control w-auto"
      />
      <button type="button" className="btn btn-secondary" ref={searchBtnRef}>
        Search
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="row text-center p-5 text-bg-body">
      <h2>No results</h2>
      <p>Search for another term or check back later for new releases.</p>
    </div>
  );
}

function SectionTitle({ title, showToggle }) {
  return (
    <header
      className="fw-bold text-uppercase py-2 d-flex gap-2 justify-content-between align-items-center"
      style={{ height: "var(--sticky-header-height)" }}
    >
      {title}
      {showToggle && <ToggleAllVersionsButton />}
    </header>
  );
}

function Badge({ release, singleBadge }) {
  const badges = releaseTypeBadges(release, singleBadge);

  return (
    <>
      {badges.map((badge, index) => (
        <span
          key={index}
          className={`badge ${badge.className} text-uppercase fw-bold me-1`}
          style={{ fontSize: "0.6rem" }}
          title={badge.text}
        >
          {badge.text}
        </span>
      ))}
    </>
  );
}

function ToggleAllVersionsButton() {
  const { settings, setSettings } = usePageContext();
  const { showAllVersions } = settings;

  return (
    <span>
      <input
        type="checkbox"
        className="btn-check"
        id="show-all-versions"
        checked={showAllVersions}
        onChange={(e) => {
          setSettings({ showAllVersions: !showAllVersions });
        }}
        autoComplete="off"
      />
      <label
        className="btn btn-sm btn-outline-secondary"
        htmlFor="show-all-versions"
        title={
          showAllVersions
            ? "Hide unsupported versions"
            : "Show unsupported versions"
        }
      >
        {showAllVersions ? Icon.hide : Icon.show}
      </label>
    </span>
  );
}

function VersionList() {
  const { settings } = usePageContext();
  const { releases, showAllVersions } = settings;

  return (
    <div className="versions-list list-group">
      {releases
        ?.filter((release) => showAllVersions || release?.supported)
        .map((release) => (
          <a
            key={release.version}
            href={`#${release.version}`}
            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
          >
            <div className="ms-2 me-auto">
              <div
                className="fw-bold d-flex align-items-center gap-1"
                title={versionTitle(release)}
              >
                {release.version}
              </div>
              <small className="text-secondary">
                {formatDate(release.date)}
              </small>
            </div>
            <div className="d-flex flex-column align-items-end">
              <Badge release={release} singleBadge={true} />
            </div>
          </a>
        ))}
    </div>
  );
}

function Sidebar() {
  return (
    <aside className="sidebar col-12 col-lg-3 align-self-start text-bg-body">
      <SectionTitle title="Versions" showToggle={true} />
      <VersionList />
    </aside>
  );
}

function ContentItem({ release }) {
  const { settings } = usePageContext();
  const copyLinkRef = useRef(null);

  const latestStableReleaseId = useMemo(() => {
    return release === settings.latestSupportedRelease
      ? "latest-stable-release"
      : null;
  }, [release.version]);

  const sanitizedVersion = useMemo(
    () => sanitizeVersion(release.version),
    [release.version],
  );

  const copyLink = useCallback((e) => {
    e.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${release.version}`;
    navigator.clipboard.writeText(url);

    copyLinkRef.current.classList.add("link-copied");

    const timeoutId = setTimeout(() => {
      copyLinkRef.current.classList.remove("link-copied");
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="list-group-item p-3" id={latestStableReleaseId}>
      <header className="d-flex justify-content-between align-items-center gap-2">
        <div>
          <h3
            title={versionTitle(release)}
            id={release.version}
            className="mb-0 d-flex align-items-center gap-2"
          >
            v{release.version}
            <button
              type="button"
              title="Click to copy the link to this version changelog section"
              onClick={copyLink}
              className="btn py-2 rounded-circle"
              ref={copyLinkRef}
            >
              {Icon.share}
            </button>
          </h3>
          <time dateTime={release.date}>{formatDate(release.date)}</time>
          <span className="d-flex align-items-center">
            <Badge release={release} />
          </span>
        </div>
        <div className="d-flex flex-column align-items-end flex-nowrap">
          {gte(sanitizedVersion, "25.0.0-alpha.0") ? (
            <DownloadDropdown release={release} os={settings.os} />
          ) : (
            <BundleDownloadButton release={release} />
          )}
        </div>
      </header>
      {release.description && (
        <div
          className="mt-4"
          dangerouslySetInnerHTML={{ __html: release.description }}
        ></div>
      )}
      <h6 className="mb-3">Supported firmwares</h6>
      <FirmwareList
        release={release}
        allReleases={settings.originalReleases}
        firmwares={release.firmwares}
        showAllVersions={settings.showAllVersions}
      ></FirmwareList>
    </div>
  );
}

function Content() {
  const { settings } = usePageContext();
  const { releases, originalReleases } = settings;

  /**
   * As the content is loaded asynchronously, user with a direct link to a specific release might
   * not land on the target section of the page. We delay the scroll to the target release until
   * the content is fully loaded.
   */
  useEffect(() => {
    if (!window.location.hash || !originalReleases?.length) return;

    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "auto" }), 0);
    }
  }, [originalReleases]);

  return (
    <div className="col">
      <div className="content-header d-flex align-items-center justify-content-between text-bg-body">
        <SectionTitle title="Changelog" />
        <SearchContent />
      </div>
      <div className="list-group">
        {releases?.map((release) => (
          <ContentItem key={release.version} release={release} />
        ))}
      </div>
    </div>
  );
}

// Main component that uses React context and renders the sidebar and content
function Page(props) {
  const { settings, setSettings } = usePageContext();
  const { releases } = settings;
  const hasReleases = useMemo(() => releases?.length > 0, [releases]);

  useEffect(() => {
    const latestSupportedRelease = props.releases?.find(
      isStableSupportedRelease,
    );
    setSettings({
      originalReleases: props.releases,
      releases: props.releases,
      latestSupportedRelease,
    });
  }, [props.releases]);

  return (
    <div className="row mb-3">
      <Sidebar />
      <Content />
      {!hasReleases && <EmptyState />}
    </div>
  );
}

// Entry (higher order) component that wraps the Page component with the PageProvider to provide context
function ProductionReleases(props) {
  return (
    <PageProvider>
      <Page releases={props.releases} />
    </PageProvider>
  );
}

export default ProductionReleases;
